import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Search, CheckCircle, Eye, X, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  info: { color: '#3182CE', bg: '#3182CE20' },
  warning: { color: '#D69E2E', bg: '#D69E2E20' },
  error: { color: '#E53E3E', bg: '#E53E3E20' },
  critical: { color: '#C53030', bg: '#C5303020' }
};

export default function SaaSErrors() {
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ severity: '', status: '', module: '' });
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => { fetchErrors(); }, [filters]);

  const fetchErrors = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.status) params.append('status', filters.status);
      if (filters.module) params.append('module', filters.module);
      const res = await axios.get(`${API}/saas/errors?${params.toString()}`);
      setErrors(res.data.items);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load errors'); }
    finally { setLoading(false); }
  };

  const updateError = async (id, status, assigned_to) => {
    try {
      const params = new URLSearchParams({ status });
      if (assigned_to) params.append('assigned_to', assigned_to);
      await axios.put(`${API}/saas/errors/${id}?${params.toString()}`);
      toast.success(`Error marked as ${status}`);
      fetchErrors();
      if (selectedError?.id === id) {
        const res = await axios.get(`${API}/saas/errors/${id}`);
        setSelectedError(res.data);
      }
    } catch { toast.error('Failed to update error'); }
  };

  return (
    <div className="saas-page" data-testid="saas-errors">
      <div className="saas-page-header">
        <div>
          <h2>System Errors</h2>
          <p>{total} errors</p>
        </div>
      </div>

      <div className="saas-filters">
        <select value={filters.severity} onChange={(e) => setFilters(p => ({ ...p, severity: e.target.value }))} className="filter-select" data-testid="filter-severity">
          <option value="">All Severities</option>
          <option value="info">Info</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
          <option value="critical">Critical</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))} className="filter-select" data-testid="filter-error-status">
          <option value="">All Statuses</option>
          <option value="unresolved">Unresolved</option>
          <option value="investigating">Investigating</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>

      <div className="saas-table-container">
        <table className="saas-table" data-testid="errors-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Severity</th>
              <th>Tenant</th>
              <th>Module</th>
              <th>Route</th>
              <th>Message</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {errors.map(err => {
              const sc = SEVERITY_CONFIG[err.severity] || SEVERITY_CONFIG.error;
              return (
                <tr key={err.id}>
                  <td className="timestamp-cell">{new Date(err.created_at).toLocaleString()}</td>
                  <td><span className="severity-badge" style={{ background: sc.bg, color: sc.color }}>{err.severity}</span></td>
                  <td>{err.tenant_name}</td>
                  <td><span className="module-badge">{err.module}</span></td>
                  <td className="route-cell">{err.route || '-'}</td>
                  <td className="message-cell" title={err.message}>{err.message?.substring(0, 50)}{err.message?.length > 50 ? '...' : ''}</td>
                  <td><span className={`status-badge ${err.status}`}>{err.status}</span></td>
                  <td>{err.assigned_to || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn" onClick={() => setSelectedError(err)} title="View Details" data-testid={`view-error-${err.id}`}><Eye size={16} /></button>
                      {err.status === 'unresolved' && (
                        <button className="action-btn" onClick={() => updateError(err.id, 'investigating')} title="Investigating" data-testid={`investigate-${err.id}`}><Clock size={16} /></button>
                      )}
                      {err.status !== 'resolved' && (
                        <button className="action-btn" onClick={() => updateError(err.id, 'resolved')} title="Resolve" data-testid={`resolve-${err.id}`}><CheckCircle size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {errors.length === 0 && !loading && <div className="empty-state"><p>No errors found</p></div>}
      </div>

      {selectedError && (
        <div className="slide-over-overlay" onClick={() => setSelectedError(null)}>
          <div className="slide-over" onClick={e => e.stopPropagation()} data-testid="error-detail">
            <div className="slide-over-header">
              <h3>Error Details</h3>
              <button className="close-btn" onClick={() => setSelectedError(null)}><X size={20} /></button>
            </div>
            <div className="slide-over-content">
              <section className="detail-section">
                <h4>Error Info</h4>
                <div className="detail-grid">
                  <div className="detail-item"><label>Severity</label><span className="severity-badge" style={{ background: SEVERITY_CONFIG[selectedError.severity]?.bg, color: SEVERITY_CONFIG[selectedError.severity]?.color }}>{selectedError.severity}</span></div>
                  <div className="detail-item"><label>Status</label><span className={`status-badge ${selectedError.status}`}>{selectedError.status}</span></div>
                  <div className="detail-item"><label>Module</label><span>{selectedError.module}</span></div>
                  <div className="detail-item"><label>Route</label><span>{selectedError.route || 'N/A'}</span></div>
                  <div className="detail-item"><label>Type</label><span>{selectedError.error_type || 'N/A'}</span></div>
                  <div className="detail-item"><label>Tenant</label><span>{selectedError.tenant_name}</span></div>
                  <div className="detail-item"><label>Created</label><span>{new Date(selectedError.created_at).toLocaleString()}</span></div>
                  {selectedError.resolved_at && <div className="detail-item"><label>Resolved</label><span>{new Date(selectedError.resolved_at).toLocaleString()}</span></div>}
                </div>
              </section>
              <section className="detail-section">
                <h4>Message</h4>
                <div className="error-message-box">{selectedError.message}</div>
              </section>
              {selectedError.stack_trace && (
                <section className="detail-section">
                  <h4>Stack Trace</h4>
                  <pre className="stack-trace-box">{selectedError.stack_trace}</pre>
                </section>
              )}
              <div className="slide-over-actions">
                {selectedError.status !== 'resolved' && (
                  <button className="btn-primary" onClick={() => { updateError(selectedError.id, 'resolved'); setSelectedError(null); }}>Mark Resolved</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
